import {FormEvent, useEffect, useState} from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import {useQuery} from '@tanstack/react-query';
import {apiClient} from '@common/http/query-client';
import {Button} from '@common/ui/buttons/button';
import {IconButton} from '@common/ui/buttons/icon-button';
import {Helmet} from '@common/seo/helmet';
import {SearchIcon} from '@common/icons/material/Search';
import {MenuIcon} from '@common/icons/material/Menu';
import {CloseIcon} from '@common/icons/material/Close';
import {TuneIcon} from '@common/icons/material/Tune';
import {FavoriteIcon} from '@common/icons/material/Favorite';
import {FavoriteBorderIcon} from '@common/icons/material/FavoriteBorder';
import {OpenInNewIcon} from '@common/icons/material/OpenInNew';
import {ShoppingCartIcon} from '@common/icons/material/ShoppingCart';
import {PersonOutlineIcon} from '@common/icons/material/PersonOutline';
import {useSettings} from '@common/core/settings/use-settings';
import {useAuth} from '@common/auth/use-auth';
import './rekaweb-gallery-page.css';
import './rekaweb-directory.css';
import './rekaweb-info-page.css';

interface ShowcaseTemplate {
  id: string;
  name: string;
  category: string;
  thumbnail: string;
  description: string;
  updated_at: string | null;
  amount: number | null;
  currency: string;
}

interface ShowcaseResponse {
  categories: string[];
  category_counts: Record<string, number>;
  published_total: number;
  featured: {name: string; thumbnail: string}[];
  pagination: {
    data: ShowcaseTemplate[];
    current_page: number;
    last_page: number;
    total: number;
  };
}

const contactUrl = '/contact';

function requestWebsiteUrl(template: ShowcaseTemplate): string {
  return `/cart?template=${encodeURIComponent(template.id)}`;
}

export function RekawebGalleryPage() {
  const {branding} = useSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('q') ?? '');
  const [menuOpen, setMenuOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [favorites, setFavorites] = useState<string[]>(() => {
    try {
      const stored: unknown = JSON.parse(localStorage.getItem('rekaweb-template-favorites') ?? '[]');
      return Array.isArray(stored)
        ? stored.filter((id): id is string => typeof id === 'string' && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(id)).slice(0, 100)
        : [];
    } catch {
      return [];
    }
  });
  const query = searchParams.get('q') ?? '';
  const category = searchParams.get('category') ?? '';
  const sort = searchParams.get('sort') ?? 'newest';
  const favoritesOnly = searchParams.get('saved') === '1';
  const requestedPage = Number(searchParams.get('page') ?? 1);
  const page = Number.isSafeInteger(requestedPage)
    ? Math.max(1, Math.min(requestedPage, 25))
    : 1;

  useEffect(() => {
    setSearch(searchParams.get('q') ?? '');
  }, [searchParams]);

  useEffect(() => {
    try {
      localStorage.setItem('rekaweb-template-favorites', JSON.stringify(favorites));
    } catch {
      // Favorites remain available for this visit when storage is restricted.
    }
  }, [favorites]);

  useEffect(() => {
    if (search.trim() === query) return;
    const timeout = window.setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (search.trim()) next.set('q', search.trim());
      else next.delete('q');
      next.delete('page');
      if (next.toString() !== searchParams.toString()) {
        setSearchParams(next, {replace: true});
      }
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [search, query, searchParams, setSearchParams]);

  const templatesQuery = useQuery({
    queryKey: ['showcase-templates', query, category, page, sort, favoritesOnly, favoritesOnly ? favorites.join(',') : ''],
    queryFn: async () => {
      const responses = await Promise.all(
        Array.from({length: page}, (_, index) =>
          apiClient.get<ShowcaseResponse>('showcase/templates', {
            params: {
              query,
              category,
              sort,
              page: index + 1,
              perPage: 24,
              ...(favoritesOnly ? {ids: favorites.length ? favorites : ['no-saved-templates']} : {}),
            },
          }),
        ),
      );
      const lastPage = responses[responses.length - 1].data;
      return {
        ...lastPage,
        pagination: {
          ...lastPage.pagination,
          data: responses.flatMap(response => response.data.pagination.data),
        },
      };
    },
  });

  const updateFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setSearchParams(next, {replace: true});
  };

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    updateFilter('q', search.trim());
  };

  const templates = templatesQuery.data?.pagination.data ?? [];
  const categories = templatesQuery.data?.categories ?? [];
  const counts = templatesQuery.data?.category_counts ?? {};
  const total = templatesQuery.data?.published_total ?? 0;
  const toggleFavorite = (id: string) => {
    setFavorites(current => current.includes(id)
      ? current.filter(value => value !== id)
      : [...current, id].slice(-100));
  };
  const clearFilters = () => {
    setSearch('');
    setSearchParams({}, {replace: true});
  };

  return (
    <div className="rekaweb-gallery rekaweb-gallery--catalogue">
      <Helmet
        tags={[
          {nodeName: 'title', _text: `Template Website Profesional Malaysia | ${branding.site_name}`},
          {
            nodeName: 'meta',
            name: 'description',
            content:
              'Terokai koleksi template website profesional untuk perniagaan, korporat, klinik, restoran dan kedai online. Pilih design pilihan anda dan Rekaweb akan siapkan website anda.',
          },
          {
            nodeName: 'meta',
            property: 'og:title',
            content: `Template Website Profesional Malaysia | ${branding.site_name}`,
          },
          {
            nodeName: 'meta',
            property: 'og:description',
            content:
              'Pilih reka bentuk website untuk perniagaan anda. Pasukan Rekaweb akan membantu membina website pilihan anda.',
          },
          {nodeName: 'meta', property: 'og:type', content: 'website'},
          {nodeName: 'link', rel: 'canonical', href: 'https://app.rekaweb.my/'},
        ]}
      />
      <Header
        menuOpen={menuOpen}
        onMenuToggle={() => setMenuOpen(!menuOpen)}
        search={search}
        onSearchChange={setSearch}
        onSearchSubmit={submitSearch}
        onFilterToggle={() => setFiltersOpen(!filtersOpen)}
        filtersOpen={filtersOpen}
        onShowAll={clearFilters}
        favoritesOnly={favoritesOnly}
        onFavoritesToggle={() => updateFilter('saved', favoritesOnly ? '' : '1')}
      />

      <main>
        <section className="rekaweb-collection-intro">
          <div className="rekaweb-collection-inner">
            <h1>{favoritesOnly ? 'PILIHAN ANDA' : category ? `${category} TEMPLATES` : 'WEBSITE TEMPLATES'}</h1>
            <p className="rekaweb-collection-description">Design Web Murah Bermula RM50. Anda Pilih Template, Upload Company Profile, Gambar Galeri, Kami Akan Siapkan Web Anda</p>
          </div>
        </section>

        <section className="rekaweb-catalogue" id="templates" aria-label="Koleksi template website">
          <div className="rekaweb-catalogue-toolbar">
            <button className="rekaweb-filter-toggle" type="button" aria-expanded={filtersOpen} aria-controls="rekaweb-filters" onClick={() => setFiltersOpen(!filtersOpen)}>
              <TuneIcon />{filtersOpen ? 'Sembunyikan filter' : 'Tunjukkan filter'}
            </button>
          </div>
          <p role="status" className="rekaweb-result-count sr-only">{templatesQuery.data?.pagination.total ?? 0} template</p>
          <div className={`rekaweb-catalogue-layout${filtersOpen ? ' filters-open' : ''}`}>
            <aside className="rekaweb-filter-sidebar" id="rekaweb-filters" aria-label="Filter template">
              <div className="rekaweb-filter-panel-title"><h2><TuneIcon />Kategori & Filter</h2><button type="button" aria-label="Tutup filter" onClick={() => setFiltersOpen(false)}><CloseIcon /></button></div>
              <form className="rekaweb-filter-search" role="search" onSubmit={submitSearch}>
                <SearchIcon aria-hidden="true" />
                <input aria-label="Cari template website" placeholder="Cari template..." maxLength={100} value={search} onChange={event => setSearch(event.target.value)} />
              </form>
              <button className="rekaweb-clear-filters" type="button" onClick={clearFilters}><CloseIcon />Kosongkan semua</button>
              <fieldset className="rekaweb-filter-group">
                <legend>Kategori</legend>
                <label><input type="checkbox" checked={!category} onChange={() => updateFilter('category', '')} /><span>Semua Template</span><small>{total}</small></label>
                {categories.map(item => (
                  <label key={item}><input type="checkbox" checked={category === item} onChange={() => updateFilter('category', category === item ? '' : item)} /><span>{item}</span><small>{counts[item] ?? 0}</small></label>
                ))}
              </fieldset>
              <fieldset className="rekaweb-filter-group">
                <legend>Pilihan Anda</legend>
                <label><input type="checkbox" checked={favoritesOnly} onChange={() => updateFilter('saved', favoritesOnly ? '' : '1')} /><span>Template disimpan</span><small>{favorites.length}</small></label>
              </fieldset>
              <label className="rekaweb-sort">Susun mengikut:
                <select aria-label="Susun template" value={sort} onChange={event => updateFilter('sort', event.target.value)}>
                  <option value="name_asc">Nama A–Z</option><option value="name_desc">Nama Z–A</option><option value="newest">Terkini</option>
                </select>
              </label>
            </aside>

            <div className="rekaweb-results">
              {templatesQuery.isError ? (
                <div className="rekaweb-empty" role="alert"><h2>Galeri tidak dapat dimuatkan.</h2><button type="button" onClick={() => templatesQuery.refetch()}>Cuba semula</button></div>
              ) : templatesQuery.isPending ? (
                <div className="rekaweb-template-grid" aria-label="Memuatkan template" role="status">{Array.from({length: 8}, (_, index) => <div className="rekaweb-card-skeleton" key={index}><div /><span /><span /></div>)}</div>
              ) : templates.length === 0 ? (
                <div className="rekaweb-empty"><SearchIcon /><h2>{favoritesOnly ? 'Belum ada template pilihan.' : !query && !category ? 'Belum ada template diterbitkan. Sila semak semula nanti.' : 'Tiada template sepadan.'}</h2><button type="button" onClick={clearFilters}>Lihat semua template</button></div>
              ) : (
                <>
                  <div className="rekaweb-template-grid">
                    {templates.map((template, index) => (
                      <article className="rekaweb-template-card" key={template.id}>
                        <div className="rekaweb-card-media">
                          <Link className="rekaweb-template-image-link" to={`/demo/${encodeURIComponent(template.id)}`} aria-label={`Lihat demo ${template.name}`}>
                            <img src={template.thumbnail} alt={`Pratonton template ${template.name}`} loading={index < 6 ? 'eager' : 'lazy'} decoding="async" />
                          </Link>
                        </div>
                        <div className="rekaweb-template-info">
                          <div className="rekaweb-card-meta">
                            {template.updated_at && <time dateTime={template.updated_at} title="Tarikh kemas kini template">{new Intl.DateTimeFormat('en-GB').format(new Date(template.updated_at))}</time>}
                            <span>{template.category}</span>
                            <button className={`rekaweb-favorite${favorites.includes(template.id) ? ' is-saved' : ''}`} type="button" aria-label={`${favorites.includes(template.id) ? 'Buang' : 'Simpan'} ${template.name}`} title={`${favorites.includes(template.id) ? 'Buang daripada' : 'Simpan dalam'} pilihan`} aria-pressed={favorites.includes(template.id)} onClick={() => toggleFavorite(template.id)}>
                              {favorites.includes(template.id) ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                            </button>
                          </div>
                          <div className="rekaweb-card-title-row">
                            <h2><Link to={`/demo/${encodeURIComponent(template.id)}`}>{template.name}</Link></h2>
                            <Link className="rekaweb-inline-demo" to={`/demo/${encodeURIComponent(template.id)}`} aria-label={`Lihat Demo ${template.name}`} title="Lihat Demo"><OpenInNewIcon /></Link>
                          </div>
                          <div className="rekaweb-card-purchase-row">
                            <span className={`rekaweb-card-price${template.amount == null ? ' is-unset' : ''}`}>
                              {template.amount == null ? 'Harga belum ditetapkan' : new Intl.NumberFormat('ms-MY', {style: 'currency', currency: template.currency}).format(template.amount / 100)}
                            </span>
                            <a className="rekaweb-inline-order" href={requestWebsiteUrl(template)} aria-label={`Budget Website (Template): ${template.name}`} title="Budget Website (Template)"><ShoppingCartIcon /></a>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                  {templatesQuery.data && page < templatesQuery.data.pagination.last_page && <div className="rekaweb-load-more-wrap"><Button variant="outline" onClick={() => updateFilter('page', String(page + 1))}>Muat Lagi</Button></div>}
                </>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

interface HeaderProps {
  menuOpen: boolean;
  onMenuToggle: () => void;
  search: string;
  onSearchChange: (value: string) => void;
  onSearchSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onFilterToggle: () => void;
  filtersOpen: boolean;
  onShowAll: () => void;
  favoritesOnly: boolean;
  onFavoritesToggle: () => void;
}

export function Header({menuOpen, onMenuToggle, search, onSearchChange, onSearchSubmit, onFilterToggle, filtersOpen, onShowAll, favoritesOnly, onFavoritesToggle}: HeaderProps) {
  const {branding, registration} = useSettings();
  const {isLoggedIn, user} = useAuth();
  return (
    <header className="rekaweb-header">
      <div className="rekaweb-header-inner">
        <Link className="rekaweb-wordmark" to="/" aria-label={`${branding.site_name}, halaman utama`}>
          <img src="/images/rekaweb-logo-white.png" alt="Rekaweb" width="180" height="48" />
        </Link>
        <IconButton
          className="rekaweb-menu-toggle"
          aria-label={menuOpen ? 'Tutup menu' : 'Buka menu'}
          aria-expanded={menuOpen}
          onClick={onMenuToggle}
        >
          {menuOpen ? <CloseIcon /> : <MenuIcon />}
        </IconButton>
        <nav className={`rekaweb-nav${menuOpen ? ' is-open' : ''}`} aria-label="Navigasi utama">
          <button type="button" onClick={onShowAll} className={!favoritesOnly ? 'is-current' : ''}>TEMPLATE</button>
          <button type="button" onClick={onFavoritesToggle} aria-pressed={favoritesOnly} className={favoritesOnly ? 'is-current' : ''}>PILIHAN</button>
          <button type="button" aria-expanded={filtersOpen} aria-controls="rekaweb-filters" onClick={onFilterToggle}>KATEGORI</button>
          <a href="https://rekaweb.my/" target="_blank" rel="noreferrer">REKAWEB</a>
          <a href={contactUrl}>HUBUNGI</a>
        </nav>
        <form className="rekaweb-header-search" role="search" onSubmit={onSearchSubmit}>
          <input aria-label="Cari template website" placeholder="Search..." value={search} maxLength={100} onChange={event => onSearchChange(event.target.value)} />
          <button type="submit" aria-label="Cari" title="Cari template"><SearchIcon /></button>
        </form>
        <div className="rekaweb-header-actions">
          <details className="rekaweb-account">
            <summary aria-label={isLoggedIn ? 'Akaun saya' : 'Log masuk atau daftar akaun'}><PersonOutlineIcon /><span>{isLoggedIn ? 'Akaun saya' : 'Akaun'}</span></summary>
            <div className="rekaweb-account-panel">
              {isLoggedIn ? <><div className="rekaweb-account-info"><strong>{user?.display_name || 'Akaun saya'}</strong><span>{user?.email}</span></div><Link to="/dashboard">Dashboard saya</Link><Link to="/account-settings">Tetapan akaun</Link></> : <><div className="rekaweb-account-info"><strong>Selamat datang ke Rekaweb</strong><span>Log masuk untuk mengurus tempahan website anda.</span></div><Link className="rekaweb-account-login" to="/login">Log masuk</Link>{!registration.disable && <Link to="/register">Daftar akaun baharu</Link>}</>}
            </div>
          </details>
          <a className="rekaweb-header-submit" href={contactUrl}>TEMPAH WEBSITE</a>
        </div>
      </div>
    </header>
  );
}

export function Footer() {
  const {isLoggedIn} = useAuth();
  const {branding} = useSettings();
  return (
    <footer className="rekaweb-footer">
      <div className="rekaweb-footer-main">
        <div>
          <Link className="rekaweb-footer-brand" to="/">{branding.site_name}</Link>
          <p>Pilihan reka bentuk website untuk perniagaan dan organisasi.</p>
        </div>
        <nav aria-label="Pautan kaki halaman">
          <a href="/#templates">Semua Template</a>
          <a href={contactUrl}>Hubungi Kami</a>
          <a href="https://rekaweb.my/" target="_blank" rel="noreferrer">Rekaweb.my</a>
          <Link to={isLoggedIn ? '/dashboard' : '/login'}>{isLoggedIn ? 'Dashboard saya' : 'Log masuk'}</Link>
        </nav>
      </div>
      <div className="rekaweb-footer-bottom"><nav className="rekaweb-footer-legal" aria-label="Maklumat dan polisi"><Link to="/pages/about-us">Tentang Kami</Link><Link to="/pages/privacy-policy">Notis Privasi</Link><Link to="/pages/terms-of-service">Terma Perkhidmatan</Link></nav><span className="rekaweb-footer-payments">Bayaran online melalui Billplz · Bayaran offline melalui pindahan bank</span><span>© 2026 Rekaweb Digital. Hak cipta terpelihara.</span></div>
    </footer>
  );
}